// app 1.5.59 (Alexander, 4 Nov 2026: "Go") — the Timelock's STAND-IN on the phone (claude/timelock.md §6, §8, §12 drop B).
//
// The rule's home is M's module on the account on Arc; until it exists the app checks before Face ID and, when a payment
// would empty a pocket, does not send it: the Worker (Worker 1.4.23, worker/src/timelock.ts) keeps the hold and plays M's
// co-signer — it says when a hold is DUE. The person's own app then sends it (the Worker never sends a payment), so the
// copy is "It can go at …" and a due hold is "ready", with Pay. Named as a stand-in: a spiked build, the Toolbox, or code
// run in M's page skips it.
//
// - On only for a wallet whose owner set the Timelock up ON THIS DEVICE (`m.tl`): the Worker's routes aren't authenticated
//   yet (drop D), so a stranger's set-up for an address changes nothing on its owner's phone.
// - The words: argon2id(phrase, salt) on the phone (the M® Note's KDF, notes/kdf.ts), k sent over TLS, the Worker keeps
//   sha256(k). Typed forgivingly: case, spaces and the ends don't matter.
// - The trigger (app 1.5.60, Alexander's Settings screen of 4 Nov 14:58): every payment over the owner's amount — $1,000,
//   $5,000 or $10,000 (placeholders; on the testing figures a dollar is a cent) — in dollars at an approximate rate (the
//   rate voucher comes with the module). 1.5.59's "more than 90 % of the pocket" is gone. The windows, the fresh payee and
//   on alert are drop C.
// - The words are the Release Word and the Lock Word (1.5.60; the codeword and the Mayday word until then).
// - What the phone shows never tells a Mayday or a wrong word from the codeword before goesAt (the Worker's view holds
//   nothing that could); after goesAt, a hold that isn't due reads "Confirming…", then "Circle Arc hasn't confirmed it yet."
import { cancelHold, getTimelock, holdPayment, holdSent, releaseHold, setTimelockPolicy, type TimelockHold, type TimelockProfile, type TimelockView } from "./api";
import { derive } from "./notes/kdf";
import { CURRENCIES } from "./fx";
import type { Hex } from "viem";

const ON_KEY = "m.tl";
const TEST_KEY = "m.tlTest";

function readOn(): Record<string, 1> {
  try { return JSON.parse(localStorage.getItem(ON_KEY) || "{}") as Record<string, 1>; } catch { return {}; }
}
/** Set up on this device for this wallet. */
export function tlOnHere(address: string): boolean { return !!readOn()[address.toLowerCase()]; }
export function setTlOnHere(address: string, on: boolean): void {
  const m = readOn();
  if (on) m[address.toLowerCase()] = 1; else delete m[address.toLowerCase()];
  try { localStorage.setItem(ON_KEY, JSON.stringify(m)); } catch { /* the Timelock stays as it was here */ }
}
/** Lab's switch: set the Timelock up with the testing figures (minutes). Per device; after the set-up the Worker keeps the
 *  wallet's choice, and moving to the testing figures later is a weakening that waits. */
export function tlTestFigures(): boolean { try { return localStorage.getItem(TEST_KEY) === "1"; } catch { return false; } }
export function setTlTestFigures(on: boolean): void { try { localStorage.setItem(TEST_KEY, on ? "1" : "0"); } catch { /* ignore */ } }

// ---- the words ---------------------------------------------------------------------------------------------------

/** What is typed, as it is compared: lower case, one space between words, no spaces at the ends. */
export const normalWord = (s: string) => s.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
export function randomSalt(): Hex {
  const b = crypto.getRandomValues(new Uint8Array(32));
  return ("0x" + [...b].map((x) => x.toString(16).padStart(2, "0")).join("")) as Hex;
}
/** k = argon2id(phrase, salt) — never stored, sent only to release. */
export async function wordKey(phrase: string, salt: Hex): Promise<Hex> {
  return (await derive(normalWord(phrase), salt)).key;
}
export async function commitment(k: Hex): Promise<Hex> {
  const bytes = new Uint8Array(k.slice(2).match(/../g)!.map((x) => parseInt(x, 16)));
  const d = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return ("0x" + [...d].map((x) => x.toString(16).padStart(2, "0")).join("")) as Hex;
}
/** The first set-up's extras: the profile and the choices made before turning it on. */
export type TlSetUp = { profile: TimelockProfile; overUsd: number; holdH: number };
/** Faster release's words: the Release Word and the Lock Word, derived with one fresh salt; only their fingerprints
 *  leave the phone. `first`: the Timelock isn't on yet — this turns it on, with these choices. */
export async function setWords(address: string, release: string, lock: string, first?: TlSetUp): Promise<TimelockView> {
  if (!normalWord(release) || !normalWord(lock)) throw new Error("words_empty");
  if (normalWord(release) === normalWord(lock)) throw new Error("words_same");
  const salt = randomSalt();
  const [kw, km] = [await wordKey(release, salt), await wordKey(lock, salt)];
  const v = await setTimelockPolicy({ address, posture: "word", ...(first ?? {}), salt, wordC: await commitment(kw), maydayC: await commitment(km) });
  setTlOnHere(address, true);
  adopt(address, v);
  return v;
}
/** Nobody can make a held payment go sooner (Faster release off), the words (on), or off. Only what changes is sent. */
export async function setPosture(address: string, posture: "nobody" | "word" | "off", first?: TlSetUp): Promise<TimelockView> {
  const v = await setTimelockPolicy({ address, posture, ...(first ?? {}) });
  if (posture !== "off") setTlOnHere(address, true);
  adopt(address, v);
  return v;
}
/** The amount, or the length of a hold. */
export async function setChoice(address: string, c: { overUsd?: number; holdH?: number }): Promise<TimelockView> {
  const v = await setTimelockPolicy({ address, ...c });
  adopt(address, v);
  return v;
}

// ---- the owner's choices, as Settings draws them -----------------------------------------------------------------

/** What the Worker offers (Worker 1.4.24 sends them too; this is the copy for a sheet drawn before its view is in). */
export const TL_OPTIONS = { overUsd: [1000, 5000, 10000], holdH: [12, 24, 48, 72], releaseH: 2 };
export const TL_DEFAULT = { overUsd: 1000, holdH: 48 };
/** On the testing figures an hour is a minute and a dollar a cent. */
export function tlAmountLabel(usd: number, profile: TimelockProfile): string {
  const n = profile === "test" ? usd / 100 : usd;
  return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
export function tlHoursLabel(h: number, profile: TimelockProfile): string {
  return `${h} ${profile === "test" ? (h === 1 ? "minute" : "minutes") : h === 1 ? "hour" : "hours"}`;
}
/** The release: the same answer whatever was typed. */
export async function release(address: string, h: TimelockHold, phrase: string, salt: Hex): Promise<number> {
  const k = await wordKey(phrase, salt);
  const { goesAt } = await releaseHold({ address, id: h.id, k });
  if (who === address) void tlRefresh();
  return goesAt;
}

// ---- the trigger -------------------------------------------------------------------------------------------------

/** Dollars per unit, roughly — enough to say whether a payment is over the owner's amount. The stand-in's approximation:
 *  the module will check the rate voucher M's FX signer already signs (timelock.md §2). */
const USD_PER: Record<string, number> = { USD: 1, EUR: 1.08, GBP: 1.27, CHF: 1.13, CAD: 0.72, AUD: 0.65, MXN: 0.054, BRL: 0.18, ZAR: 0.055, SEK: 0.094, KRW: 0.00072 };
export const usdOf = (amount: number, code: string) => amount * (USD_PER[code] ?? 1);
/** Every payment over the owner's amount is held (in dollars; the figures carry it on the wallet's scale). */
export function wouldHold(amount: number, code: string, f: { OVER_USD?: number }): boolean {
  return f.OVER_USD != null && usdOf(amount, code) > f.OVER_USD + 1e-9;
}

// ---- the words on screen -----------------------------------------------------------------------------------------

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const hhmm = (d: Date) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
/** "16:20" today; "14:20 on Thursday" within the week; "14:20 on 12 Nov" beyond. */
export function whenText(at: number, now = Date.now()): string {
  const d = new Date(at), n = new Date(now);
  const day0 = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day0(d) - day0(n)) / 86_400_000);
  if (days <= 0) return hhmm(d);
  if (days < 7) return `${hhmm(d)} on ${DAYS[d.getDay()]}`;
  return `${hhmm(d)} on ${d.getDate()} ${d.toLocaleString("en-GB", { month: "short" })}`;
}
/** The button's words: "Send at 16:20" today, "Send on Thursday" later. */
export function whenShort(at: number, now = Date.now()): string {
  const t = whenText(at, now);
  return t.includes(" on ") ? `on ${t.split(" on ")[1]}` : `at ${t}`;
}
/** The pill's countdown: 47:59:58, then 9:59. */
export function countdown(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60, p = (x: number) => String(x).padStart(2, "0");
  return h > 0 ? `${h}:${p(m)}:${p(r)}` : `${m}:${p(r)}`;
}
/** A hold's currency and amount, from the token it names. */
export function holdMoney(h: TimelockHold): { code: string; amount: number } {
  const c = CURRENCIES.find((x) => x.token?.address.toLowerCase() === h.token.toLowerCase());
  const dec = c?.token?.decimals ?? 6;
  return { code: c?.code ?? "USD", amount: Number(h.amount) / 10 ** dec };
}

/** Where a hold stands on this phone. `confirming`/`stuck`: past goesAt and not due — a Mayday, a wrong word, or (for a
 *  moment) the next read not in yet; the phone can't tell which, and shows no number. */
export type HoldPhase = "held" | "released" | "confirming" | "stuck" | "ready";
export const STUCK_AFTER_MS = 30_000;
export function phaseOf(h: TimelockHold, now: number): HoldPhase {
  if (h.due) return "ready";
  if (now < h.goesAt) return h.releasedAt ? "released" : "held";
  return now < h.goesAt + STUCK_AFTER_MS ? "confirming" : "stuck";
}

// ---- the home's copy of the Worker's view ------------------------------------------------------------------------

type Listener = () => void;
let view: TimelockView | null = null;
let skew = 0; // the Worker's clock minus ours
let who: string | null = null;
let timer: number | undefined;
let wake: number | undefined;
const listeners = new Set<Listener>();
const tell = () => listeners.forEach((f) => { try { f(); } catch { /* one painter's failure isn't the others' */ } });

/** A view the Worker has just answered with (a change made in Settings): the home's copy takes it at once. */
function adopt(address: string, v: TimelockView): void {
  if (!tlOnHere(address)) return;
  if (who !== address) { tlStart(address); }
  view = v;
  tell();
}
/** Now, on the Worker's clock (the times in a view are its). */
export const tlNow = () => Date.now() + skew;
export const tlView = () => (who ? view : null);
/** The hold the home shows: the soonest to go. */
export function tlPrimary(): TimelockHold | null {
  const v = tlView();
  if (!v?.holds.length) return null;
  return [...v.holds].sort((a, b) => (a.due === b.due ? a.goesAt - b.goesAt : a.due ? -1 : 1))[0];
}
export function onTimelock(f: Listener): () => void { listeners.add(f); return () => listeners.delete(f); }

/** Read the Worker's view for the wallet the home shows; keep reading while a hold is open (every 5 s, and just after the
 *  next goesAt). Off on this device: nothing is read. */
export async function tlRefresh(): Promise<void> {
  if (!who) return;
  const a = who;
  window.clearTimeout(wake);
  try {
    const t0 = Date.now();
    const v = await getTimelock(a);
    if (who !== a) return;
    skew = v.now - Math.round((t0 + Date.now()) / 2);
    view = v;
  } catch { /* a failed read keeps the last view; the next tick asks again */ }
  tell();
  const next = view?.holds.map((h) => h.goesAt).filter((t) => t > tlNow()).sort((x, y) => x - y)[0];
  if (next !== undefined && next - tlNow() < 5_000) wake = window.setTimeout(() => void tlRefresh(), next - tlNow() + 400);
}
export function tlStart(address: string): void {
  tlStop();
  if (!tlOnHere(address)) return;
  who = address;
  void tlRefresh();
  timer = window.setInterval(() => {
    if (document.visibilityState !== "visible") return;
    if (view?.holds.length) void tlRefresh();
    else tell();
  }, 5_000);
}
export function tlStop(): void {
  window.clearInterval(timer); window.clearTimeout(wake);
  timer = wake = undefined;
  who = null; view = null;
}

// ---- the actions -------------------------------------------------------------------------------------------------

export async function holdThis(address: string, b: { token: string; amount: string; to: string; ref?: string }): Promise<TimelockHold> {
  const { hold } = await holdPayment({ address, ...b, trigger: "amount" });
  if (who === address) void tlRefresh();
  return hold;
}
export async function cancelThis(address: string, h: TimelockHold): Promise<void> {
  await cancelHold({ address, id: h.id });
  if (who === address) void tlRefresh();
}
/** The person's app paid a due hold: the Worker closes it (at acceptance, so a dead tab doesn't offer it again). */
export async function paidThis(address: string, id: string, hash: string): Promise<void> {
  await holdSent({ address, id, txHash: hash });
  if (who === address) void tlRefresh();
}
